import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { Box, Divider, Stack, Typography } from '@mui/material'
import T from 'prop-types'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { isRepairSession } from 'lib/sessionLifecycle'

import ConfirmBar from 'components/atoms/ConfirmBar'
import DotCell from 'components/atoms/DotCell'
import DownloadBar from 'components/atoms/DownloadBar'
import DropArea from 'components/atoms/DropArea'
import FilterSelect from 'components/atoms/FilterSelect'
import SearchField from 'components/atoms/SearchField'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import TitleBar from 'components/atoms/TitleBar'
import UploadBar from 'components/atoms/UploadBar'

import useCarsFilters from 'hooks/useCarsFilters'
import useInputOutput, {
  BASE_FILE_UPLOAD_EXTENSIONS,
  validateExpectedLabel,
  validateNotEmpty,
} from 'hooks/useInputOutput'

import SessionsDownloadTemplate from '../Sessions/SessionsDownloadTemplate'
import {
  GROUPS_TOTAL,
  checkPrices,
  hasPricingData,
} from '../Sessions/sessionsLib'
import SessionsSearchBar from '../Sessions/SessionsSearchBar'

const createColumns = (t, cars, carsFilters, requiredGroups) => {
  const {
    filters,
    filtersData,
    setBrands,
    setModels,
    setExists,
    setExistsPrice,
    setPlateQuery,
  } = carsFilters
  const columns = [
    {
      accessor: 'brand',
      Header: (
        <TableHeaderCell
          label={t('sessions.pricing.table.brand')}
          filter={
            <FilterSelect
              data-testid="search-brand"
              options={filtersData.brands}
              value={filters.brands}
              handleChange={setBrands}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchCarBrand')}
            />
          }
        />
      ),
      sticky: 'left',
    },
    {
      accessor: 'model',
      Header: (
        <TableHeaderCell
          label={t('sessions.pricing.table.model')}
          filter={
            <FilterSelect
              data-testid="search-models"
              options={filtersData.models}
              value={filters.models}
              handleChange={setModels}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchCarModel')}
            />
          }
        />
      ),
      sticky: 'left',
    },
    {
      accessor: 'plate',
      Header: (
        <TableHeaderCell
          label={t('sessions.pricing.table.plate')}
          filter={
            <SearchField
              data-testid="search-plate"
              placeholder={t('common.form.searchCarPlate')}
              value={filters.plateQuery}
              onChange={(event) => setPlateQuery(event.target.value)}
              fullWidth={false}
            />
          }
        />
      ),
      sticky: 'left',
      width: 190,
      // width: 92,
    },
    {
      accessor: 'priceCheck',
      Header: (
        <TableHeaderCell
          label={t('sessions.pricing.table.priceCheck')}
          errorsNumber={
            cars.filter((car) => !checkPrices(car, requiredGroups)).length
          }
          filter={
            <FilterSelect
              data-testid="search-price-check"
              options={filtersData.existsPrice}
              value={filters.existsPrice}
              handleChange={setExistsPrice}
            />
          }
        />
      ),
      Cell: ({ value: priceCheck }) => (
        <DotCell
          color={priceCheck ? 'primary' : 'error'}
          label={
            priceCheck
              ? t('sessions.pricing.priceOk')
              : t('sessions.error.priceMissing')
          }
        />
      ),
      sticky: 'left',
      width: 160,
    },
    {
      accessor: 'exists',
      Header: (
        <TableHeaderCell
          label={t('sessions.pricing.table.plateCheck')}
          errorsNumber={cars.filter((car) => !filters.existsCheck(car)).length}
          filter={
            <FilterSelect
              data-testid="search-plate-check"
              options={filtersData.exists}
              value={filters.exists}
              handleChange={setExists}
            />
          }
        />
      ),
      Cell: ({ value: exists }) => (
        <DotCell
          color={exists ? 'primary' : 'error'}
          label={
            exists
              ? t('sessions.pricing.carFoundOk')
              : t('sessions.error.plateMissing')
          }
        />
      ),
      sticky: 'left',
      width: 154,
    },
    {
      accessor: 'reservePrice',
      Header: t('sessions.reservePrice'),
      width: 120,
    },
  ]

  columns.push(
    ...requiredGroups
      .filter((group) => group)
      .map((group) => ({
        accessor: `group${group}`,
        Header: t('sessions.group', {
          attribute: group,
        }),
        width: 92,
      }))
  )

  return columns
}

export default function SessionsPricing({
  session,
  stepCars,
  updatePrices,
  handleIngestPrices,
  handleValidation,
  handleConfirm,
  confirmed,
  readOnly,
  loading = false,
  contentIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const { step } = useParams()
  const repairSession = isRepairSession(session)

  const groupsTotal = useMemo(
    () => session.requiredGroups.filter((group) => group).length,
    [session.requiredGroups]
  )

  const carsFilters = useCarsFilters({
    startFilters: {
      existsCheck: (car) => car.exists && car.existsInCars,
    },
  })
  const { filters, initFilters, setQuery, filterCars, resetFilters } =
    carsFilters

  useEffect(() => {
    if (!stepCars.length) {
      return
    }

    initFilters(stepCars)
  }, [stepCars, initFilters])

  const columns = useMemo(
    () => createColumns(t, stepCars, carsFilters, session.requiredGroups),
    [t, stepCars, carsFilters, session.requiredGroups]
  )

  const uploaded = useMemo(
    () => stepCars.some((car) => hasPricingData(session, car)),
    [session, stepCars]
  )

  const validateNumber = (value, row, column) => {
    if (value !== 0 && (!value || isNaN(value))) {
      return false
    }
    return true
  }

  const parser = (rows) => {
    validateExpectedLabel(
      rows,
      1,
      1,
      t('common.excel.plate'),
      'common.error.fileUpload.wrongColumnName',
      t
    )

    validateExpectedLabel(
      rows,
      1,
      2,
      t('common.excel.reservePrice'),
      'common.error.fileUpload.wrongColumnName',
      t
    )
    return rows
      .slice(2)
      .map((row, i) =>
        row.length && validateNotEmpty(rows, i + 2, 1)
          ? {
              plate: row[1].toString().trim().toUpperCase(),
              prices: Array.from(row.slice(2), (price, k) => {
                return (validateNumber(price) && Number(price)) || null
              }),
            }
          : null
      )
      .filter((item) => item)
  }

  const handleUpdate = useCallback(
    (cars, fileName) => {
      updatePrices(session?.sessionId, cars, fileName)
      resetFilters()
    },
    [resetFilters, session?.sessionId, updatePrices]
  )

  const handleIngest = useCallback(() => {
    handleIngestPrices(session?.sessionId)
    resetFilters()
  }, [handleIngestPrices, session?.sessionId, resetFilters])

  const { handleDrop, handleSelectFile, saveXls } = useInputOutput(
    handleUpdate,
    parser
  )

  const handleDownload = async () => {
    const titlesRow = [t('common.excel.plate'), t('common.excel.reservePrice')]
    for (let i = 1; i <= GROUPS_TOTAL; i++) {
      titlesRow.push(
        t('sessions.group', {
          attribute: i,
        })
      )
    }
    titlesRow.push(
      t('common.excel.priceCheck'),
      t('common.excel.plateCheck'),
      t('common.excel.name')
    )

    const cars = uploaded ? preparedItems : []
    const rows = [
      titlesRow,
      ...cars.map((car) => [
        car.plate,
        ...car.prices,
        car.priceCheck
          ? t('sessions.pricing.priceOk')
          : t('sessions.error.priceMissing'),
        car.exists
          ? t('sessions.pricing.carFoundOk')
          : t('sessions.error.plateMissing'),
        `${car.title}`,
      ]),
    ]
    saveXls('prices', rows)
  }

  const preparedItems = useMemo(() => {
    const numberFormatter = new Intl.NumberFormat('it-IT')

    return stepCars.map((car) => {
      const exists = filters.existsCheck(car)

      return {
        ...car,
        priceCheck: checkPrices(car, session.requiredGroups),
        exists,
        reservePrice: car.prices[0] && numberFormatter.format(car.prices[0]),
        ...session.requiredGroups
          .filter((group) => group)
          .reduce(
            (previous, current) => ({
              ...previous,
              [`group${current}`]:
                car.prices[current] &&
                numberFormatter.format(car.prices[current]),
            }),
            {}
          ),
      }
    })
  }, [stepCars, filters, session.requiredGroups])

  const filteredCars = useMemo(
    () => filterCars(preparedItems, filters),
    [preparedItems, filterCars, filters]
  )

  return (
    <div data-testid="session-pricing">
      <TitleBar
        title={t(
          repairSession
            ? 'sessions.pricing.repair.contentTitle'
            : 'sessions.pricing.contentTitle'
        )}
        description={t(
          repairSession
            ? 'sessions.pricing.repair.contentDescription'
            : 'sessions.pricing.contentDescription'
        )}
        rightColumn={
          <UploadBar
            id={step}
            fileName={session?.[`${step}FileName`]}
            readOnly={readOnly || contentIsReadOnly}
            loading={loading}
            uploaded={uploaded}
            handleEmptyList={() => handleUpdate(session.sessionId, [])}
            handleSelectFile={handleSelectFile}
            onIngest={handleIngest}
            ingestLabel={t(`common.form.ingestButtonPrice`)}
          />
        }
      />
      {uploaded ? (
        <>
          <Table
            id="cars-list"
            items={filteredCars}
            columns={columns}
            top={
              <>
                <ConfirmBar
                  component="div"
                  label={t(`sessions.pricing.confirmButton`)}
                  left={
                    <>
                      <SessionsSearchBar
                        id="text"
                        label={t('sessions.pricing.platesUploaded')}
                        filteredLabel={t('common.filters.filteredPlates')}
                        cars={uploaded ? stepCars : []}
                        filteredCars={filteredCars}
                        query={filters.query}
                        handleChangeSearch={(event) =>
                          setQuery(event.target.value)
                        }
                      />
                    </>
                  }
                  leftProps={{ sx: { width: 0.5 } }}
                  right={
                    <>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        flex="1"
                      >
                        {groupsTotal < session.requiredGroups.length && (
                          <Stack direction="row" alignItems="center">
                            <Box pr={4} />
                            <WarningAmberRoundedIcon
                              color="secondary"
                              sx={{ bgcolor: 'error.main', borderRadius: 1 }}
                            />
                            <Box pr={1} />
                            <Typography variant="body2" color="error">
                              {t('sessions.pricing.partnerNotAssigned')}
                            </Typography>
                          </Stack>
                        )}
                        <DownloadBar
                          readOnly={readOnly || contentIsReadOnly}
                          handleDownload={handleDownload}
                        />
                      </Stack>
                    </>
                  }
                  rightProps={{
                    sx: {
                      width:
                        groupsTotal < session.requiredGroups.length
                          ? 0.5
                          : undefined,
                      justifyContent: 'space-between',
                    },
                  }}
                  disabled={
                    !handleValidation() ||
                    confirmed ||
                    readOnly ||
                    contentIsReadOnly
                  }
                  handleConfirm={handleConfirm}
                />
                <Divider />
              </>
            }
          />
        </>
      ) : (
        <>
          <DropArea
            onDrop={handleDrop}
            disabled={readOnly || contentIsReadOnly}
            accept={BASE_FILE_UPLOAD_EXTENSIONS.toString()}
          />
          <SessionsDownloadTemplate
            label={t('sessions.pricing.downloadTemplateFile')}
            handleDownload={handleDownload}
          />
        </>
      )}
    </div>
  )
}

SessionsPricing.propTypes = {
  session: T.object.isRequired,
  stepCars: T.array.isRequired,
  updatePrices: T.func.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  confirmed: T.bool,
  readOnly: T.bool,
  loading: T.bool,
  contentIsReadOnly: T.bool,
}
